export type ObjectiveStatus = "pending" | "success" | "fail";

export type MissionSnapshot = {
  squadAlive: number;
  squadTotal: number;
  squadCompleted: boolean;
  enemiesAlive: number;
  totalEnemiesSpawned: number;  // guards NeutralizeAll from winning before first wave
  // Seconds since the mission went active (intro excluded)
  elapsed: number;
  // Primary hard targets (armor + flak sites): alive now / authored total
  primariesAlive: number;
  primariesTotal: number;
  // Convoy units that reached their exit (AMBUSH fail pressure)
  convoyEscaped: number;
  // RESCUE: the trapped element's strength, and whether the main squad has
  // reached them (after link-up they move as one and can no longer be lost)
  trappedAlive: number;
  linkedUp: boolean;
};

export interface Objective {
  readonly label: string;
  check(snap: MissionSnapshot): ObjectiveStatus;
}

// Primary win condition: squad reaches its final waypoint
export class SquadReachDestination implements Objective {
  readonly label: string;

  constructor(label = "ESCORT SQUAD TO DELTA") {
    this.label = label;
  }

  check(snap: MissionSnapshot): ObjectiveStatus {
    return snap.squadCompleted ? "success" : "pending";
  }
}

// Fail condition: too many squad members lost
export class SquadSurvival implements Objective {
  readonly label: string;

  constructor(
    private readonly minSurvivors: number,
    label?: string,
  ) {
    this.label = label ?? `KEEP ${minSurvivors}+ ALIVE`;
  }

  check(snap: MissionSnapshot): ObjectiveStatus {
    return snap.squadAlive < this.minSurvivors ? "fail" : "pending";
  }
}

// Optional bonus condition: all enemies eliminated
export class NeutralizeAll implements Objective {
  readonly label: string;

  constructor(label = "NEUTRALIZE ALL THREATS") {
    this.label = label;
  }

  check(snap: MissionSnapshot): ObjectiveStatus {
    if (snap.totalEnemiesSpawned === 0) return "pending"; // no wave yet
    return snap.enemiesAlive === 0 ? "success" : "pending";
  }
}

// DEFEND win: survive on station until the clock runs out.
export class HoldOut implements Objective {
  readonly label: string;

  constructor(private readonly seconds: number, label?: string) {
    this.label = label ?? `HOLD FOR ${Math.round(seconds)}S`;
  }

  check(snap: MissionSnapshot): ObjectiveStatus {
    return snap.elapsed >= this.seconds ? "success" : "pending";
  }
}

// DESTROY/AMBUSH fail: the window closes.
export class TimeLimit implements Objective {
  readonly label: string;

  constructor(private readonly seconds: number, label?: string) {
    this.label = label ?? `WINDOW: ${Math.round(seconds)}S`;
  }

  check(snap: MissionSnapshot): ObjectiveStatus {
    return snap.elapsed > this.seconds ? "fail" : "pending";
  }
}

// DESTROY/AMBUSH win: every primary hard target (armor, AA) destroyed.
export class DestroyPrimaries implements Objective {
  readonly label: string;

  constructor(label = "DESTROY ALL PRIMARY TARGETS") {
    this.label = label;
  }

  check(snap: MissionSnapshot): ObjectiveStatus {
    if (snap.primariesTotal === 0) return "pending"; // nothing authored yet
    return snap.primariesAlive === 0 ? "success" : "pending";
  }
}

// RESCUE fail: the trapped element died before the link-up.
export class TrappedSurvival implements Objective {
  readonly label: string;

  constructor(label = "KEEP THE TRAPPED ELEMENT ALIVE") {
    this.label = label;
  }

  check(snap: MissionSnapshot): ObjectiveStatus {
    if (snap.linkedUp) return "pending"; // aboard — safe with Alpha now
    return snap.trappedAlive < 1 ? "fail" : "pending";
  }
}

// AMBUSH fail: too many convoy vehicles slipped through.
export class ConvoyIntercept implements Objective {
  readonly label: string;

  constructor(private readonly maxEscapes: number, label?: string) {
    this.label = label ?? `MAX ${maxEscapes} ESCAPES`;
  }

  check(snap: MissionSnapshot): ObjectiveStatus {
    return snap.convoyEscaped > this.maxEscapes ? "fail" : "pending";
  }
}
